import { FareCode } from "@silversea-ssc/web-fares";
import React, { useCallback } from "react";
import {
  BlockContentValue,
  Button,
  FareCardBase,
  FareCardBaseContent,
  FareCardBaseProps,
  FareCardFooter,
  FareCardFooterProps,
  FareCardHeader,
  FareCardRefundability,
  isBlockContentValuePresent
} from "@silversea-ssc/ui-components-react";
import { InclusionCode } from "@silversea-ssc/web-inclusions";
import type { TokenValues } from "@silversea-ssc/dotcom-read-model-types/Tokens";
import { CruiseGroupCode } from "@silversea-ssc/web-cruise-groups";
import {
  computeFareCardInfo,
  maybeShowFooterNotesForNonAirCountries
} from "./utils";
import FareCardInfographic from "./FareCardInfographic";
import { useFarePenaltiesModal } from "components/FarePenaltiesModal/FarePenaltiesModalProvider";
import FareCardDisclaimer from "./FareCardDisclaimer";
import { useGeolocation } from "src/utils/localization/geolocation/GeolocationContext";
import { isPresent } from "src/utils/checks";
import { useLingui } from "@lingui/react/macro";

export type FareCardProps = Omit<FareCardBaseProps, "trackingName"> & {
  title: string | undefined;
  headline: string | undefined;
  description: string | undefined;
  fareCode: FareCode;
  servicesDisclaimer?: BlockContentValue;

  enabledInclusions: readonly InclusionCode[];
  inclusionsContentTokens?: TokenValues;
  cruiseGroup?: CruiseGroupCode;
  ctaButton?: FareCardFooterProps["ctaButton"];
  variant: "collapsed" | "expanded";
  footerNotesForNonAirCountries?: BlockContentValue;
};

const FareCard: React.FC<FareCardProps> = ({
  title,
  headline,
  description,
  fareCode,
  servicesDisclaimer,
  enabledInclusions,
  inclusionsContentTokens,
  cruiseGroup,
  ctaButton,
  variant,
  footerNotesForNonAirCountries,
  ...otherProps
}) => {
  const { onRequestOpenPenaltiesModal, remotePaymentTermByFareCode } =
    useFarePenaltiesModal();
  const geolocation = useGeolocation();
  const { t } = useLingui();
  const paymentTerms = remotePaymentTermByFareCode(fareCode);
  const maybeFooterNotesForNonAirCountries =
    maybeShowFooterNotesForNonAirCountries(
      geolocation.airCountry,
      footerNotesForNonAirCountries
    );

  const {
    cancellationPolicyButtonLabel,
    depositRefundabilityContent,
    refundCreditExchangeFooterNotes,
    isRefundable
  } = computeFareCardInfo({ t, cruiseGroup, paymentTerms });

  const onPenaltiesModalClick = useCallback(() => {
    onRequestOpenPenaltiesModal({
      fareCode
    });
  }, [fareCode, onRequestOpenPenaltiesModal]);

  return (
    <React.Fragment>
      <FareCardBase trackingName={`infographic_${fareCode}`} {...otherProps}>
        <FareCardHeader
          title={title || ""}
          subtitle={headline}
          description={description}
        >
          {depositRefundabilityContent && (
            <div>
              <FareCardRefundability
                isRefundable={isRefundable}
                content={depositRefundabilityContent}
              />
            </div>
          )}
        </FareCardHeader>
        <FareCardBaseContent>
          <FareCardInfographic
            cruiseGroup={cruiseGroup}
            enabledInclusions={enabledInclusions}
            fareCode={fareCode}
            inclusionsContentTokens={inclusionsContentTokens || {}}
            expandable={variant == "expanded"}
            isExpanded={variant == "expanded"}
          />
        </FareCardBaseContent>
        <FareCardFooter
          ctaButton={ctaButton}
          withSeparator={
            isBlockContentValuePresent(maybeFooterNotesForNonAirCountries) ||
            isBlockContentValuePresent(servicesDisclaimer) ||
            isPresent(refundCreditExchangeFooterNotes)
          }
        >
          {isBlockContentValuePresent(maybeFooterNotesForNonAirCountries) && (
            <FareCardDisclaimer content={maybeFooterNotesForNonAirCountries} />
          )}
          <FareCardDisclaimer content={servicesDisclaimer} />
          <FareCardDisclaimer content={refundCreditExchangeFooterNotes} />

          <Button
            renderAs="button"
            variant="inline-text"
            onClick={onPenaltiesModalClick}
            dataContentPiece="read_our_cancellation_policy"
            noWrap
          >
            {cancellationPolicyButtonLabel}
          </Button>
        </FareCardFooter>
      </FareCardBase>
    </React.Fragment>
  );
};

export default FareCard;
